import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import MyLoadingButton from "../../../../components/buttons/MyLoadingButton";
import { ThemeForm } from "../../../../components/buttons/ThemeButton";
import CustomAccordion from "../../../../components/display/CustomAccordion";
import UserHint from "../../../../components/display/UserHint";
import LanguageSwitcher from "../../../../components/LanguageSwitcher";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import * as API_PATH from "../../../../enums/ApiPaths";
import { CALENDAR_VIEW } from "../../../../enums/LocalStorageValues";
import useUser from "../../../../hooks/useUser";
import {
  isNullOrEmpty,
  isPasswordEmpty,
  isEmptyString,
  navigateToPhoneSetup,
} from "../../../../utils/Functions";
import usePutClinic from "../../hooks/usePutClinic";
import usePutProfile from "../../hooks/usePutProfile";
import DeleteAccount from "../DeleteAccount";
import EmailEdit from "../EmailEdit";
import PasswordChange from "../PasswordChange";
import ChangeCalendarView from "./ChangeCalendarView";
import ChangeClinic from "./ChangeClinic";
import ChangeTableSize from "./ChangeTableSize";
import { useQueryClient } from "@tanstack/react-query";
import { LOGGED_IN_USER } from "../../../../enums/QueryKeys";
import { useNavigate } from "react-router-dom";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import { snackbarSeverity } from "../../../../hooks/useSnackbar";

export const userPreferencesCheckboxes = {
  sendAppointmentEmailsWhenUpdated: "sendAppointmentEmailsWhenUpdated",
  sendAppointmentEmails: "sendAppointmentEmails",
  sendInboxEmails: "sendInboxEmails",
  disableAllEmails: "disableAllEmails",
};

const emailChange = {
  title: "EmailChange",
  component: (
    <Box>
      <EmailEdit />
    </Box>
  ),
  showTitle: true,
};
const passwordChange = {
  title: "PasswordChange",
  component: (
    <Box>
      <PasswordChange />
    </Box>
  ),
  showTitle: true,
};

const deleteBody = {
  title: "DeleteAccount",
  component: (
    <Box>
      <DeleteAccount />
    </Box>
  ),
  showTitle: true,
};

export default function Settings() {
  const { i18n, t } = useTranslation();
  const { roles, booleans, profile, preferance, currentClinic } = useUser();
  const queryClient = useQueryClient();
  const noEmail = isNullOrEmpty(profile?.email) && roles.employee;
  const { mode } = React.useContext(ThemeContext);
  const put = usePutProfile(API_PATH.EDIT_USER_PREFERENCES + profile?.id);
  const putClinic = usePutClinic(profile?.id);
  const navigate = useNavigate();
  const { handleSnackbar } = React.useContext(SnackbarContext);

  const methods = useForm({
    defaultValues: {
      defaultTheme: preferance.defaultTheme || "dark",
      defaultLanguage: preferance.defaultLanguage,
      defaultCalendarView: preferance.defaultCalendarView,
      appointmentTableRowsPerPage: preferance.appointmentTableRowsPerPage || 10,
      clinicUsersTableRowsPerPage: preferance.clinicUsersTableRowsPerPage || 10,
      sendAppointmentEmailsWhenUpdated:
        preferance.sendAppointmentEmailsWhenUpdated,
      sendAppointmentEmails: preferance.sendAppointmentEmails,
      sendInboxEmails: preferance.sendInboxEmails,
      disableAllEmails: preferance.disableAllEmails,
    },
  });

  const themeBody = {
    title: "defaultTheme",
    component: (
      <FormProvider {...methods}>
        <Box>
          <ThemeForm color="inherit" testId="profile.settings.theme-toggle" />
        </Box>
      </FormProvider>
    ),
    showTitle: true,
  };

  const langBody = {
    title: "defaultLanguage",
    component: (
      <LanguageSwitcher
        defaultLanguage={preferance.defaultLanguage}
        testIdPrefix="profile.settings.language"
      />
    ),
    showTitle: true,
  };

  const calendarView = {
    title: "defaultCalendarView",
    component: (
      <FormProvider {...methods}>
        <ChangeCalendarView
          defaultCalendarView={preferance.defaultCalendarView}
          testId="profile.settings.calendar-view-input"
        />
      </FormProvider>
    ),
    showTitle: false,
  };

  const tableSizeChange = {
    title: "tableSizeChange",
    component: (
      <FormProvider {...methods}>
        <ChangeTableSize
          title={"appointmentTableSize"}
          defaultTableSize={preferance.appointmentTableRowsPerPage}
          name={"appointmentTableRowsPerPage"}
          testId="profile.settings.appointment-table-size-select"
        />
      </FormProvider>
    ),
    showTitle: false,
  };
  const tableSizeChangeClinic = {
    title: "tableSizeChangeClinicUsers",
    component: (
      <FormProvider {...methods}>
        <ChangeTableSize
          title={"clinicUsersTableSize"}
          defaultTableSize={preferance.clinicUsersTableRowsPerPage}
          name={"clinicUsersTableRowsPerPage"}
          testId="profile.settings.clinic-users-table-size-select"
        />
      </FormProvider>
    ),
    showTitle: false,
  };
  const changeClinic = {
    title: "ChangeClinic",
    component: (
      <FormProvider {...methods}>
        <ChangeClinic />
      </FormProvider>
    ),
    showTitle: false,
  };

  const sendEmailNotifications = {
    title: "WantToReceiveEmailNotificationsAboutAppointments",
    component: (
      <FormProvider {...methods}>
        <SendEmailNotif checked={booleans.sendEmailNotifications} />
      </FormProvider>
    ),
    textColor: "Gray",
    showTitle: true,
  };

  const sendAppointmentEmails = {
    title: userPreferencesCheckboxes.sendAppointmentEmails,
    component: (
      <FormProvider {...methods}>
        <CheckboxForm
          checked={preferance.sendAppointmentEmails}
          name={userPreferencesCheckboxes.sendAppointmentEmails}
          label={userPreferencesCheckboxes.sendAppointmentEmails}
          key={userPreferencesCheckboxes.sendAppointmentEmails}
        />
      </FormProvider>
    ),
    showTitle: true,
    info: <UserHint iconOnly hint={"SendAppointmentEmailsUserHint"} />,
  };

  const sendAppointmentEmailsWhenUpdated = {
    title: userPreferencesCheckboxes.sendAppointmentEmailsWhenUpdated,
    component: (
      <FormProvider {...methods}>
        <CheckboxForm
          checked={preferance.sendAppointmentEmailsWhenUpdated}
          name={userPreferencesCheckboxes.sendAppointmentEmailsWhenUpdated}
          label={userPreferencesCheckboxes.sendAppointmentEmailsWhenUpdated}
          key={userPreferencesCheckboxes.sendAppointmentEmailsWhenUpdated}
        />
      </FormProvider>
    ),
    showTitle: true,
    info: (
      <UserHint iconOnly hint={"SendAppointmentEmailsWhenUpdatedUserHint"} />
    ),
  };

  const sendInboxEmails = {
    title: userPreferencesCheckboxes.sendInboxEmails,
    component: (
      <FormProvider {...methods}>
        <CheckboxForm
          checked={preferance.sendInboxEmails}
          name={userPreferencesCheckboxes.sendInboxEmails}
          label={userPreferencesCheckboxes.sendInboxEmails}
          key={userPreferencesCheckboxes.sendInboxEmails}
        />
      </FormProvider>
    ),
    showTitle: true,
    info: <UserHint iconOnly hint={"SendInboxEmailsUserHint"} />,
  };

  const disableAllEmails = {
    title: userPreferencesCheckboxes.disableAllEmails,
    component: (
      <FormProvider {...methods}>
        <CheckboxForm
          checked={preferance.disableAllEmails}
          name={userPreferencesCheckboxes.disableAllEmails}
          label={userPreferencesCheckboxes.disableAllEmails}
          key={userPreferencesCheckboxes.disableAllEmails}
        />
      </FormProvider>
    ),
    showTitle: true,
    info: <UserHint iconOnly hint={"disableAllEmailsUserHint"} />,
  };

  const accountTable = {
    title: "TablePreferences",
    component: (
      <Box>
        <Stack spacing={3} sx={{ mt: 1.5 }}>
          {calendarView.component}
          <Stack spacing={3}>
            {roles.adminOrEmployee && tableSizeChange.component}
            {(roles.adminOrEmployee || roles.master) &&
              tableSizeChangeClinic.component}
          </Stack>
        </Stack>
      </Box>
    ),
    expanded: true,
    showTitle: true,
  };

  const hasEmailBody = [emailChange, passwordChange];
  const noEmailBody = [passwordChange];

  const preferences = {
    title: "Preferences",
    items: [
      themeBody,
      langBody,
      ...(roles?.user ? [sendEmailNotifications] : [accountTable]),
    ],
    expanded: true,
  };

  const account = {
    title: "Account",
    items: [
      ...(noEmail || isPasswordEmpty() === "true" ? noEmailBody : hasEmailBody),
    ],
    expanded: true,
  };

  const userSettings = [
    {
      title: "EmailsAndInbox",
      items: [
        sendAppointmentEmails,
        sendAppointmentEmailsWhenUpdated,
        sendInboxEmails,
        disableAllEmails,
      ],
      expanded: true,
    },
    {
      title: "Clinic",
      items: [changeClinic],
      expanded: true,
    },
  ];

  const permanent = [
    {
      title: "PermanentActions",
      items: [deleteBody],
      expanded: false,
    },
  ];

  // Group settings into logical sections
  const settingsGroups = [
    preferences,
    ...(roles?.user ? userSettings : []),
    account,
    ...(!roles?.master ? permanent : []),
  ];

  const submitData = async (e) => {
    const sanitizedAppointmentTableRowsPerPage = roles?.adminOrEmployee
      ? (e.appointmentTableRowsPerPage ??
        preferance.appointmentTableRowsPerPage ??
        10) // non-admin: force null
      : null;

    const sanitizedClinicUsersTableRowsPerPage =
      roles?.adminOrEmployee || roles?.master
        ? (e.clinicUsersTableRowsPerPage ??
          preferance.clinicUsersTableRowsPerPage ??
          10)
        : null;

    const data = {
      theme: e.defaultTheme,
      language: i18n.language,
      calendarView: e.defaultCalendarView ? e.defaultCalendarView : "agenda",
      sendAppointmentEmails: e.sendAppointmentEmails,
      sendAppointmentEmailsWhenUpdated: e.sendAppointmentEmailsWhenUpdated,
      appointmentTableRowsPerPage: sanitizedAppointmentTableRowsPerPage,
      clinicUsersTableRowsPerPage: sanitizedClinicUsersTableRowsPerPage,
      disableAllEmails: e.disableAllEmails,
      sendInboxEmails: e.sendInboxEmails,
    };

    const res = await put.submitCallback(data);

    if (res) {
      localStorage.setItem(CALENDAR_VIEW, data?.calendarView);
      queryClient.invalidateQueries([LOGGED_IN_USER]);
    }

    if (
      e?.clinic !== null &&
      e?.clinic !== undefined &&
      currentClinic?.id !== e?.clinic?.id
    ) {
      await putClinic.submitCallback(e?.clinic.id);
      localStorage.setItem(CALENDAR_VIEW, data?.calendarView);
    }
    if (res && isEmptyString(profile.phoneNumber)) {
      handleSnackbar(
        "CantUseApplicationBeforeAddingAPhoneNumber",
        snackbarSeverity.WARNING,
        "outlined",
      );
      navigateToPhoneSetup(navigate);
    }
  };

  return (
    <form onSubmit={methods.handleSubmit(submitData)}>
      <Stack spacing={3}>
        {settingsGroups.map((group, groupIndex) => (
          <CustomAccordion
            key={groupIndex}
            title={group.title}
            defaultExpanded={group.expanded}
            testId={`profile.settings.group.${group.title}`}
          >
            <Stack spacing={2}>
              {group.items.map((item, itemIndex) => (
                <React.Fragment key={itemIndex}>
                  <Stack spacing={1}>
                    {item.showTitle ? (
                      <Typography
                        variant="body2"
                        color={item.textColor || "text.secondary"}
                      >
                        {t(item.title)}
                      </Typography>
                    ) : null}
                    {item?.info ? (
                      <Stack
                        direction={"row"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                      >
                        {item.component}
                        {item?.info}
                      </Stack>
                    ) : (
                      item.component
                    )}
                  </Stack>
                  {itemIndex < group.items.length - 1 && (
                    <Divider sx={{ my: 1 }} />
                  )}
                </React.Fragment>
              ))}
            </Stack>
          </CustomAccordion>
        ))}
        <Box>
          <MyLoadingButton
            message={t("SaveChanges")}
            testId="profile.settings.save-button"
          />
        </Box>
      </Stack>
    </form>
  );
}

function SendEmailNotif({ checked = false }) {
  const { control } = useFormContext();
  return (
    <Controller
      name=""
      disabled={true}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          label=""
          {...field}
          control={<Checkbox checked={checked} />}
        />
      )}
    />
  );
}

const formValuesToDisable = [
  "sendAppointmentEmailsWhenUpdated",
  "sendAppointmentEmails",
  "sendInboxEmails",
];

export function CheckboxForm({ checked = false, name = "", label = "" }) {
  const { control, setValue, watch } = useFormContext();
  const { t } = useTranslation();

  const disableAllEmailsVal = watch("disableAllEmails");

  React.useEffect(() => {
    if (disableAllEmailsVal) {
      formValuesToDisable.forEach((val) => setValue(val, false));
    }
  }, [disableAllEmailsVal, setValue]);

  function getIsDisabled() {
    return (
      disableAllEmailsVal && name != userPreferencesCheckboxes.disableAllEmails
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          label={t(label)}
          control={
            <Checkbox
              disabled={getIsDisabled()}
              checked={!!field.value} // bind to form state
              onChange={(e) => field.onChange(e.target.checked)}
              inputProps={{
                "data-testid": `profile.settings.checkbox.${name}`,
              }}
            />
          }
        />
      )}
    />
  );
}
